import { getAddress } from "@components/Address";
import { getCurrencyValue } from "@components/Amount";
import { formatPhoneNumber } from "@components/Text";
import { getUsDateFromIsoDate } from "@components/Date";
import {
  isTrustInvestment,
  isSdiraInvestment,
  isJointInvestment,
  isEntityInvestment,
  isIndividualInvestment,
  computeInvestmentAmount,
} from "@components/Investment";
import {
  PROJECT_TYPES,
  ENTITY_TYPE_LLC,
  PROFILE_TYPES_LABELS,
  ENTITY_TYPE_TITLES_MAP,
  INVESTMENT_PROFILE_FIELD_MAP,
  ENTITY_TYPE_EXEMPT_ORGANIZATION,
  ENTITY_TAX_FILING_STATUS_TITLES_MAP,
  JOINT_MEMBERSHIP_INTERESTS_TITLES_MAP,
  ENTITY_MEMBERSHIP_INTERESTS_TITLES_MAP,
  ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_LLC,
  ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_IRA,
  BENEFICIAL_OWNER_TAX_FILING_STATUS_TITLES_MAP,
  ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_TITLES_MAP,
  ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_INDIVIDUAL,
} from "@components/Domain";
import {
  CUSTODIAN_REVIEWED_SDIRA,
  CHECKBOOK_CONTROLLED_SDIRA,
} from "@api/services/investments/shapes/SdiraTypeEnum";

import getBeneficiary from "./getBeneficiary";
import getEntityNames from "./getEntityNames";
import getUsResidenceState from "./getUsResidenceState";
import getAccreditation, { ACCREDITATION_VALUE_NO, ACCREDITATION_VALUE_YES } from "./getAccreditation";
import type {
  Column,
  Account,
  Project,
  Property,
  Investment,
  ProfileField,
  BankAccountsMap,
  ResidencyProfile,
} from "../types";

const SDIRA_TYPE_LABELS_MAP: Record<string, string> = {
  [CUSTODIAN_REVIEWED_SDIRA]: "Custodian-reviewed SDIRA",
  [CHECKBOOK_CONTROLLED_SDIRA]: "Checkbook-controlled SDIRA",
};

const EMPTY_VALUE = "-";

const LABEL_NO = "No";
const LABEL_YES = "Yes";
const LABEL_SIGNED = "SIGNED";
const LABEL_PENDING = "PENDING";

const LABEL_US_CITIZEN = "is US citizen";
const LABEL_NOT_US_CITIZEN = "is not US citizen";

const LABEL_NO_ACCREDITATION = "No Accreditation";
const LABEL_ACCREDITATION_VERIFIED = "Accreditation Verified";
const LABEL_UNVERIFIED_ACCREDITATION = "Unverified Accreditation";

const LABEL_SINGLE_PERSON = "Individually, as a single person";
const LABEL_MARRIED_PERSON = "A married person, as my sole and separate property";

const LABEL_DISREGARDED = "Disregarded for federal income tax purposes";
const LABEL_NOT_DISREGARDED = "Not disregarded for federal income tax purposes";

const MEMBERSHIP_TITLE_PREFIX = "As ";
const NAME_PARTS_SEPARATOR = " ";
const DETAILS_SEPARATOR = ", ";

const WHITESPACE_REGEX = /\s+/g;

type GetAccount = (accountId: string) => Account;
type GetProperty = (propertyId: string) => Property;
type GetSponsorName = (sponsorId?: string) => string;
type ComputeUserFullname = (userId?: string) => string;

type Options = {
  project: Project;
  getAccount: GetAccount;
  properties: Property[];
  getProperty: GetProperty;
  classColumns?: Column[];
  getSponsorName: GetSponsorName;
  bankAccountsMap: BankAccountsMap;
  computeUserFullname: ComputeUserFullname;
};

export const formatName = (name: string): string => {
  const formattedName = name.replace(WHITESPACE_REGEX, NAME_PARTS_SEPARATOR).trim();

  return formattedName;
};

const getAccreditationValue = (accreditationValue: string): string => {
  const isAccreditation = accreditationValue === ACCREDITATION_VALUE_YES;
  const isNoAccreditation = accreditationValue === ACCREDITATION_VALUE_NO;

  if (isNoAccreditation) {
    return LABEL_NO_ACCREDITATION;
  }

  if (isAccreditation) {
    return LABEL_ACCREDITATION_VERIFIED;
  }

  return LABEL_UNVERIFIED_ACCREDITATION;
};

const getProfile = (investment: Investment): ResidencyProfile | undefined => {
  const { profileType } = investment;

  const profileField = INVESTMENT_PROFILE_FIELD_MAP[profileType] as ProfileField;
  const profile = investment[profileField];

  return profile;
};

const getInvestmentExportColumns = ({
  classColumns = [],
  project,
  getAccount,
  properties,
  getProperty,
  getSponsorName,
  bankAccountsMap,
  computeUserFullname,
}: Options): Column[] => {
  const {
    type: projectType,
    name: projectName,
    closeDate: projectCloseDate,
  } = project;

  const isNonFund = projectType !== PROJECT_TYPES.FUND;

  const getInvestor = (investorAccountId: string) => {
    const account = getAccount(investorAccountId);
    const { investor } = account;

    return investor;
  };

  return [
    {
      title: "Project Name",
      value: () => `${projectName}`.trim(),
    },
    {
      title: "Entity Name(s)",
      value: () => getEntityNames(properties, getProperty, project),
    },
    {
      title: "Beneficiary",
      value: () => getBeneficiary(bankAccountsMap, project),
    },
    {
      title: "Sponsor",
      value: ({ sponsorId }) => getSponsorName(sponsorId),
    },
    {
      title: "Liaison",
      value: ({ brokerUserId }) => computeUserFullname(brokerUserId),
    },
    {
      title: "Investment ID",
      value: ({ id }) => id,
    },
    {
      title: "Investment Profile Name",
      value: ({ name }) => name,
    },
    {
      title: "Investor Legal Full Name",
      value: ({ investorAccountId }) => {
        const { givenName, familyName, additionalName } = getInvestor(investorAccountId);

        const fullName = [ givenName, additionalName, familyName ].join(NAME_PARTS_SEPARATOR);

        return formatName(fullName);
      },
    },
    {
      title: "DOB",
      value: ({ investorAccountId }) => {
        const { dateOfBirth } = getInvestor(investorAccountId);

        const formattedDate = dateOfBirth
          ? getUsDateFromIsoDate(dateOfBirth)
          : "";

        return formattedDate;
      },
    },
    {
      title: "Investor Address",
      value: ({ investorAccountId }) => {
        const { address } = getInvestor(investorAccountId);

        return getAddress(address);
      },
    },
    {
      title: "Contact Phone Number",
      value: ({ investorAccountId }) => {
        const { mobileNumber } = getInvestor(investorAccountId);

        return formatPhoneNumber(mobileNumber);
      },
    },
    {
      title: "Email Address",
      value: ({ investorAccountId }) => {
        const { email } = getInvestor(investorAccountId);

        return email;
      },
    },
    {
      title: "Additional Details",
      value: (investment) => {
        const { isFunded, investor } = investment;
        const { hasAccreditation, accreditationExpiresAt } = investor;

        const {
          value: accreditationValue,
        } = getAccreditation(hasAccreditation, accreditationExpiresAt, projectCloseDate, isFunded, isNonFund);

        const accreditation = getAccreditationValue(accreditationValue);

        const profile = getProfile(investment);

        if (!profile) {
          return accreditation;
        }

        const { isUsCitizen } = profile;

        const hasUsCitizenValue = isUsCitizen !== undefined;

        if (!hasUsCitizenValue) {
          return accreditation;
        }

        const citizenship = isUsCitizen
          ? LABEL_US_CITIZEN
          : LABEL_NOT_US_CITIZEN;

        return [ accreditation, citizenship ].join(DETAILS_SEPARATOR);
      },
    },
    {
      title: "Investment Profile Type",
      value: ({ profileType }) => PROFILE_TYPES_LABELS[profileType] || "",
    },
    {
      title: "Title of membership interests",
      value: (investment) => {
        const { profileIndividual, profileJoint, profileEntity } = investment;

        const isIndividual = isIndividualInvestment(investment);

        if (isIndividual) {
          if (!profileIndividual) {
            return undefined;
          }

          const { isMarriedAndSeparateProperty } = profileIndividual;

          const title = isMarriedAndSeparateProperty
            ? LABEL_MARRIED_PERSON
            : LABEL_SINGLE_PERSON;

          return title;
        }

        const isJoint = isJointInvestment(investment);

        if (isJoint) {
          if (!profileJoint) {
            return undefined;
          }

          const { membershipInterestsTitle } = profileJoint;

          return JOINT_MEMBERSHIP_INTERESTS_TITLES_MAP[membershipInterestsTitle];
        }

        const isEntity = isEntityInvestment(investment);

        if (isEntity) {
          if (!profileEntity) {
            return undefined;
          }

          const { membershipInterestsTitle } = profileEntity;

          const title = ENTITY_MEMBERSHIP_INTERESTS_TITLES_MAP[membershipInterestsTitle]?.replace(MEMBERSHIP_TITLE_PREFIX, "");

          return title;
        }

        return EMPTY_VALUE;
      },
    },
    {
      title: "SSN/ITIN",
      value: (investment) => {
        const { profileJoint, profileIndividual } = investment;

        const isIndividual = isIndividualInvestment(investment);

        if (isIndividual) {
          if (!profileIndividual) {
            return undefined;
          }

          return profileIndividual.ssnNumber;
        }

        const isJoint = isJointInvestment(investment);

        if (isJoint) {
          if (!profileJoint) {
            return undefined;
          }

          return profileJoint.ssnNumber;
        }

        return EMPTY_VALUE;
      },
    },
    {
      title: "Co-Investor Legal Full Name",
      value: (investment) => {
        const isJoint = isJointInvestment(investment);

        if (!isJoint) {
          return EMPTY_VALUE;
        }

        const { profileJoint } = investment;

        if (!profileJoint) {
          return undefined;
        }

        const {
          jointGivenName,
          jointFamilyName,
          jointAdditionalName,
        } = profileJoint;

        return [ jointGivenName, jointAdditionalName, jointFamilyName ].join(NAME_PARTS_SEPARATOR);
      },
    },
    {
      title: "Co-Investor Email",
      value: (investment) => {
        const isJoint = isJointInvestment(investment);

        if (!isJoint) {
          return EMPTY_VALUE;
        }

        const { profileJoint } = investment;

        if (!profileJoint) {
          return undefined;
        }

        const { shouldCoInvestorSign } = profileJoint;

        if (!shouldCoInvestorSign) {
          return EMPTY_VALUE;
        }

        return profileJoint.jointEmail;
      },
    },
    {
      title: "Joint Address",
      value: (investment) => {
        const isJoint = isJointInvestment(investment);

        if (!isJoint) {
          return EMPTY_VALUE;
        }

        const { profileJoint } = investment;

        if (!profileJoint) {
          return undefined;
        }

        return getAddress(profileJoint.jointAddress);
      },
    },
    {
      title: "Entity's Full Legal Name",
      value: (investment) => {
        const isEntity = isEntityInvestment(investment);

        if (!isEntity) {
          return EMPTY_VALUE;
        }

        const isTrust = isTrustInvestment(investment);

        if (isTrust) {
          return EMPTY_VALUE;
        }

        const { profileEntity } = investment;

        if (!profileEntity) {
          return undefined;
        }

        return profileEntity.name;
      },
    },
    {
      title: "Entity's Address",
      value: (investment) => {
        const isEntity = isEntityInvestment(investment);

        if (!isEntity) {
          return EMPTY_VALUE;
        }

        const isTrust = isTrustInvestment(investment);

        if (isTrust) {
          return EMPTY_VALUE;
        }

        const { profileEntity } = investment;

        if (!profileEntity) {
          return undefined;
        }

        const {
          address,
          isDisregardedForTaxPurposes,
        } = profileEntity;

        if (isDisregardedForTaxPurposes) {
          return EMPTY_VALUE;
        }

        return getAddress(address);
      },
    },
    {
      title: "Trust's Full Legal Name",
      value: (investment) => {
        const isEntity = isEntityInvestment(investment);

        if (!isEntity) {
          return EMPTY_VALUE;
        }

        const isTrust = isTrustInvestment(investment);

        if (!isTrust) {
          return EMPTY_VALUE;
        }

        const { profileEntity } = investment;

        if (!profileEntity) {
          return undefined;
        }

        return profileEntity.name;
      },
    },
    {
      title: "Trust's Address",
      value: (investment) => {
        const isEntity = isEntityInvestment(investment);

        if (!isEntity) {
          return EMPTY_VALUE;
        }

        const isTrust = isTrustInvestment(investment);

        if (!isTrust) {
          return EMPTY_VALUE;
        }

        const { profileEntity } = investment;

        if (!profileEntity) {
          return undefined;
        }

        const {
          address,
          isDisregardedForTaxPurposes,
        } = profileEntity;

        if (isDisregardedForTaxPurposes) {
          return EMPTY_VALUE;
        }

        return getAddress(address);
      },
    },
    {
      title: "EIN",
      value: (investment) => {
        const isEntity = isEntityInvestment(investment);

        if (!isEntity) {
          return EMPTY_VALUE;
        }

        const { profileEntity } = investment;

        if (!profileEntity) {
          return undefined;
        }

        const { hasEinNumber } = profileEntity;

        if (!hasEinNumber) {
          return EMPTY_VALUE;
        }

        return profileEntity.einNumber;
      },
    },
    {
      title: "Entity Type",
      value: (investment) => {
        const isEntity = isEntityInvestment(investment);

        if (!isEntity) {
          return EMPTY_VALUE;
        }

        const { profileEntity } = investment;

        if (!profileEntity) {
          return undefined;
        }

        const isTrust = isTrustInvestment(investment);

        if (isTrust) {
          return EMPTY_VALUE;
        }

        const { entityType, isDisregardedForTaxPurposes } = profileEntity;

        if (isDisregardedForTaxPurposes) {
          return EMPTY_VALUE;
        }

        return ENTITY_TYPE_TITLES_MAP[entityType];
      },
    },
    {
      title: "Tax Filing Status",
      value: (investment) => {
        const isEntity = isEntityInvestment(investment);

        if (!isEntity) {
          return EMPTY_VALUE;
        }

        const { profileEntity } = investment;

        if (!profileEntity) {
          return undefined;
        }

        const {
          entityType,
          taxFilingStatus,
          isDisregardedForTaxPurposes,
        } = profileEntity;

        const isLLC = entityType === ENTITY_TYPE_LLC;

        if (!isLLC) {
          return EMPTY_VALUE;
        }

        if (isDisregardedForTaxPurposes) {
          return EMPTY_VALUE;
        }

        const title = taxFilingStatus
          ? ENTITY_TAX_FILING_STATUS_TITLES_MAP[taxFilingStatus]
          : "";

        return title;
      },
    },
    {
      title: "Retirement Plan (IRA/SEP/Keogh/etc.)",
      value: (investment) => {
        const isEntity = isEntityInvestment(investment);

        if (!isEntity) {
          return EMPTY_VALUE;
        }

        const { profileEntity } = investment;

        if (!profileEntity) {
          return undefined;
        }

        const {
          entityType,
          isRetirementPlan,
        } = profileEntity;

        const isExemptOrganization = entityType === ENTITY_TYPE_EXEMPT_ORGANIZATION;

        if (!isExemptOrganization) {
          return EMPTY_VALUE;
        }

        const hasRetirementPlanValue = isRetirementPlan !== undefined;

        if (!hasRetirementPlanValue) {
          return "";
        }

        const label = isRetirementPlan
          ? LABEL_YES
          : LABEL_NO;

        return label;
      },
    },
    {
      title: "Status (e.g. Disregarded or not)",
      value: (investment) => {
        const isEntity = isEntityInvestment(investment);

        if (!isEntity) {
          return EMPTY_VALUE;
        }

        const { profileEntity } = investment;

        if (!profileEntity) {
          return undefined;
        }

        const { isDisregardedForTaxPurposes } = profileEntity;

        const hasDisregardedForTaxPurposesValue = isDisregardedForTaxPurposes !== undefined;

        if (!hasDisregardedForTaxPurposesValue) {
          return "";
        }

        const label = isDisregardedForTaxPurposes
          ? LABEL_DISREGARDED
          : LABEL_NOT_DISREGARDED;

        return label;
      },
    },
    {
      title: "Beneficial Owner Name",
      value: (investment) => {
        const isEntity = isEntityInvestment(investment);

        if (!isEntity) {
          return EMPTY_VALUE;
        }

        const { profileEntity } = investment;

        if (!profileEntity) {
          return undefined;
        }

        const { isDisregardedForTaxPurposes } = profileEntity;

        const name = isDisregardedForTaxPurposes
          ? profileEntity.beneficialOwnerName
          : EMPTY_VALUE;

        return name;
      },
    },
    {
      title: "Beneficial Owner Entity Type",
      value: (investment) => {
        const isEntity = isEntityInvestment(investment);

        if (!isEntity) {
          return EMPTY_VALUE;
        }

        const { profileEntity } = investment;

        if (!profileEntity) {
          return undefined;
        }

        const { isDisregardedForTaxPurposes } = profileEntity;

        if (!isDisregardedForTaxPurposes) {
          return EMPTY_VALUE;
        }

        const { beneficialOwnerEntityType } = profileEntity;

        const title = beneficialOwnerEntityType
          ? ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_TITLES_MAP[beneficialOwnerEntityType]
          : "";

        return title;
      },
    },
    {
      title: "Beneficial Owner Tax Filing Status",
      value: (investment) => {
        const isEntity = isEntityInvestment(investment);

        if (!isEntity) {
          return EMPTY_VALUE;
        }

        const { profileEntity } = investment;

        if (!profileEntity) {
          return undefined;
        }

        const {
          beneficialOwnerEntityType,
          isDisregardedForTaxPurposes,
          beneficialOwnerTaxFilingStatus,
        } = profileEntity;

        const isLLC = beneficialOwnerEntityType === ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_LLC;

        if (!isLLC) {
          return EMPTY_VALUE;
        }

        if (!isDisregardedForTaxPurposes) {
          return EMPTY_VALUE;
        }

        const title = beneficialOwnerTaxFilingStatus
          ? BENEFICIAL_OWNER_TAX_FILING_STATUS_TITLES_MAP[beneficialOwnerTaxFilingStatus]
          : "";

        return title;
      },
    },
    {
      title: "Beneficial Owner EIN",
      value: (investment) => {
        const isEntity = isEntityInvestment(investment);

        if (!isEntity) {
          return EMPTY_VALUE;
        }

        const { profileEntity } = investment;

        if (!profileEntity) {
          return undefined;
        }

        const {
          beneficialOwnerEntityType,
          isDisregardedForTaxPurposes,
        } = profileEntity;

        if (!isDisregardedForTaxPurposes) {
          return EMPTY_VALUE;
        }

        const isIRA = beneficialOwnerEntityType === ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_IRA;
        const isIndividual = beneficialOwnerEntityType === ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_INDIVIDUAL;

        if (isIRA) {
          return EMPTY_VALUE;
        }

        if (isIndividual) {
          return EMPTY_VALUE;
        }

        return profileEntity.beneficialOwnerEinNumber;
      },
    },
    {
      title: "Beneficial Owner Custodian EIN",
      value: (investment) => {
        const isEntity = isEntityInvestment(investment);

        if (!isEntity) {
          return EMPTY_VALUE;
        }

        const { profileEntity } = investment;

        if (!profileEntity) {
          return undefined;
        }

        const {
          beneficialOwnerEntityType,
          isDisregardedForTaxPurposes,
        } = profileEntity;

        if (!isDisregardedForTaxPurposes) {
          return EMPTY_VALUE;
        }

        const isIRA = beneficialOwnerEntityType === ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_IRA;

        if (!isIRA) {
          return EMPTY_VALUE;
        }

        return profileEntity.beneficialOwnerCustodianEinNumber;
      },
    },
    {
      title: "Beneficial Owner SSN/ITIN",
      value: (investment) => {
        const isEntity = isEntityInvestment(investment);

        if (!isEntity) {
          return EMPTY_VALUE;
        }

        const { profileEntity } = investment;

        if (!profileEntity) {
          return undefined;
        }

        const {
          hasSsn,
          isDisregardedForTaxPurposes,
        } = profileEntity;

        if (!isDisregardedForTaxPurposes) {
          return EMPTY_VALUE;
        }

        if (!hasSsn) {
          return EMPTY_VALUE;
        }

        return profileEntity.beneficialOwnerSsnNumber;
      },
    },
    {
      title: "Beneficial Owner Address",
      value: (investment) => {
        const isEntity = isEntityInvestment(investment);

        if (!isEntity) {
          return EMPTY_VALUE;
        }

        const { profileEntity } = investment;

        if (!profileEntity) {
          return undefined;
        }

        const { isDisregardedForTaxPurposes } = profileEntity;

        const address = isDisregardedForTaxPurposes
          ? getAddress(profileEntity.beneficialOwnerAddress)
          : EMPTY_VALUE;

        return address;
      },
    },
    {
      title: "SDIRA Type",
      value: (investment) => {
        const isSdira = isSdiraInvestment(investment);

        if (!isSdira) {
          return EMPTY_VALUE;
        }

        const { profileSdira } = investment;

        if (!profileSdira) {
          return undefined;
        }

        const { sdiraType } = profileSdira;

        return SDIRA_TYPE_LABELS_MAP[sdiraType] || "";
      },
    },
    {
      title: "Entity’s Full Legal Name",
      value: (investment) => {
        const isSdira = isSdiraInvestment(investment);

        if (!isSdira) {
          return EMPTY_VALUE;
        }

        const { profileSdira } = investment;

        if (!profileSdira) {
          return undefined;
        }

        const {
          sdiraType,
          entityName,
        } = profileSdira;

        const isCheckbook = sdiraType === CHECKBOOK_CONTROLLED_SDIRA;

        if (!isCheckbook) {
          return EMPTY_VALUE;
        }

        return entityName;
      },
    },
    {
      title: "Entity has EIN",
      value: (investment) => {
        const isSdira = isSdiraInvestment(investment);

        if (!isSdira) {
          return EMPTY_VALUE;
        }

        const { profileSdira } = investment;

        if (!profileSdira) {
          return undefined;
        }

        const {
          sdiraType,
          hasEinNumber,
        } = profileSdira;

        const isCheckbook = sdiraType === CHECKBOOK_CONTROLLED_SDIRA;

        if (!isCheckbook) {
          return EMPTY_VALUE;
        }

        const label = hasEinNumber
          ? LABEL_YES
          : LABEL_NO;

        return label;
      },
    },
    {
      title: "Entity EIN",
      value: (investment) => {
        const isSdira = isSdiraInvestment(investment);

        if (!isSdira) {
          return EMPTY_VALUE;
        }

        const { profileSdira } = investment;

        if (!profileSdira) {
          return undefined;
        }

        const {
          sdiraType,
          einNumber,
          hasEinNumber,
        } = profileSdira;

        const isCheckbook = sdiraType === CHECKBOOK_CONTROLLED_SDIRA;

        if (!isCheckbook) {
          return EMPTY_VALUE;
        }

        if (!hasEinNumber) {
          return EMPTY_VALUE;
        }

        return einNumber;
      },
    },
    {
      title: "Full Legal SDIRA Account Name",
      value: (investment) => {
        const isSdira = isSdiraInvestment(investment);

        if (!isSdira) {
          return EMPTY_VALUE;
        }

        const { profileSdira } = investment;

        if (!profileSdira) {
          return undefined;
        }

        return profileSdira.accountName;
      },
    },
    {
      title: "Custodian Name",
      value: (investment) => {
        const isSdira = isSdiraInvestment(investment);

        if (!isSdira) {
          return EMPTY_VALUE;
        }

        const { profileSdira } = investment;

        if (!profileSdira) {
          return undefined;
        }

        return profileSdira.custodianName;
      },
    },
    {
      title: "Custodian EIN",
      value: (investment) => {
        const isSdira = isSdiraInvestment(investment);

        if (!isSdira) {
          return EMPTY_VALUE;
        }

        const { profileSdira } = investment;

        if (!profileSdira) {
          return undefined;
        }

        return profileSdira.custodianEinNumber;
      },
    },
    {
      title: "Custodian Address",
      value: (investment) => {
        const isSdira = isSdiraInvestment(investment);

        if (!isSdira) {
          return EMPTY_VALUE;
        }

        const { profileSdira } = investment;

        if (!profileSdira) {
          return undefined;
        }

        return getAddress(profileSdira.custodianAddress);
      },
    },
    {
      title: "State of Residence",
      value: (investment) => {
        let region: string | undefined;
        let profile: ResidencyProfile | null = null;
        let isEntity = false;

        const {
          investor,
          profileJoint,
          profileSdira,
          profileEntity,
          profileIndividual,
        } = investment;

        const isIndividual =
          isIndividualInvestment(investment) &&
          !!profileIndividual;

        if (isIndividual) {
          profile = profileIndividual;

          const { address } = investor;
          region = address?.addressRegion;
        }

        const isJoint =
          isJointInvestment(investment) &&
          !!profileJoint;

        if (isJoint) {
          profile = profileJoint;

          region = (profileJoint.jointAddress || {}).addressRegion;
        }

        const isEntityProfile =
          isEntityInvestment(investment) &&
          !!profileEntity;

        if (isEntityProfile) {
          profile = profileEntity;
          isEntity = true;

          const { address, beneficialOwnerAddress } = profileEntity;
          region = address?.addressRegion || beneficialOwnerAddress?.addressRegion;
        }

        const isSdira =
          isSdiraInvestment(investment) &&
          !!profileSdira;

        if (isSdira) {
          const { address } = investor;

          return address?.addressRegion || "";
        }

        if (profile) {
          return getUsResidenceState(isEntity, profile, region);
        }

        return "";
      },
    },
    {
      title: "Non-US Person",
      value: (investment) => {
        const isSdira = isSdiraInvestment(investment);

        if (isSdira) {
          return LABEL_NO;
        }

        const profile = getProfile(investment);

        if (!profile) {
          return "";
        }

        const { isUsPerson, isUsCitizen } = profile;

        if (isUsCitizen) {
          return LABEL_NO;
        }

        const hasUsPersonValue = isUsPerson !== undefined;

        if (!hasUsPersonValue) {
          return "";
        }

        if (isUsPerson) {
          return LABEL_NO;
        }

        return LABEL_YES;
      },
    },
    {
      title: "Distribution Account Mask",
      value: ({ receiverSourceName }) => receiverSourceName,
    },
    {
      title: "Subscription Status",
      value: ({ isSigned }) => {
        const status = isSigned
          ? LABEL_SIGNED
          : LABEL_PENDING;

        return status;
      },
    },
    ...classColumns,
    {
      title: "Total Committed",
      value: (investment) => {
        const amount = computeInvestmentAmount(investment);

        return getCurrencyValue(amount);
      },
    },
    {
      title: "Funds Received",
      value: ({ receivedAmount }) => getCurrencyValue(receivedAmount),
    },
  ];
};

export default getInvestmentExportColumns;
